/**
 * lazy-storage.ts
 *
 * Custom storage implementation for Jotai atomWithStorage that defers localStorage operations
 * to improve initial load performance.
 *
 * Primary responsibilities:
 * - Provide a drop-in replacement for default Jotai storage
 * - Defer writes to localStorage to avoid blocking main thread
 * - Batch storage operations for improved performance
 * - Handle reading from localStorage at appropriate times
 */

import { createJSONStorage } from 'jotai/utils'
import type { SyncStorage } from 'jotai/vanilla/utils/atomWithStorage'

// Time (in ms) to delay writes to localStorage
const WRITE_DELAY = 100

// In-memory cache to avoid reading from localStorage repeatedly
const memoryCache = new Map<string, any>()

// Queue for batching writes
const writeQueue = new Map<string, { key: string; value: any; timer: NodeJS.Timeout }>()

// Type-safe storage implementation
export interface LazyStorage<Value> extends SyncStorage<Value> {
  getItem: (key: string, initialValue: Value) => Value
  setItem: (key: string, value: Value) => void
  removeItem: (key: string) => void
  subscribe?: (key: string, callback: (value: Value) => void) => () => void
}

/**
 * Creates a lazy storage implementation that defers writes to localStorage
 * and reads from memory when possible.
 *
 * @param getOnInit Whether to get item from storage on initialization (defaults to false for performance)
 * @returns Storage implementation compatible with atomWithStorage
 */
export const createLazyJSONStorage = <T>(getOnInit = false): LazyStorage<T> => {
  // The base storage uses localStorage
  const baseStorage = createJSONStorage<T>(() => localStorage)

  return {
    getItem: (key: string, initialValue: T): T => {
      // If we've already loaded this value into memory, use it
      if (memoryCache.has(key)) {
        return memoryCache.get(key)
      }

      // Otherwise, decide whether to load from localStorage
      if (getOnInit) {
        try {
          // Get from localStorage
          const value = baseStorage.getItem(key, initialValue)
          // Cache the value
          memoryCache.set(key, value)
          return value
        } catch (error) {
          console.error(`Error reading ${key} from localStorage:`, error)
          return initialValue
        }
      } else {
        // For performance, don't read from localStorage on init
        // This is the key performance improvement for first load
        memoryCache.set(key, initialValue)

        // Schedule an async read for later to sync with localStorage
        setTimeout(() => {
          try {
            const value = baseStorage.getItem(key, initialValue)
            memoryCache.set(key, value)
          } catch (error) {
            console.error(`Error reading ${key} from localStorage:`, error)
          }
        }, 0)

        return initialValue
      }
    },

    setItem: (key: string, value: T): void => {
      // Always update the memory cache immediately
      memoryCache.set(key, value)

      // Clear any pending write for this key
      if (writeQueue.has(key)) {
        clearTimeout(writeQueue.get(key)!.timer)
        writeQueue.delete(key)
      }

      // Schedule a new write with delay
      const timer = setTimeout(() => {
        try {
          baseStorage.setItem(key, value)
          writeQueue.delete(key)
        } catch (error) {
          console.error(`Error writing ${key} to localStorage:`, error)
        }
      }, WRITE_DELAY)

      writeQueue.set(key, { key, value, timer })
    },

    removeItem: (key: string): void => {
      // Remove from memory cache
      memoryCache.delete(key)

      // Clear any pending write
      if (writeQueue.has(key)) {
        clearTimeout(writeQueue.get(key)!.timer)
        writeQueue.delete(key)
      }

      // Schedule removal
      setTimeout(() => {
        try {
          baseStorage.removeItem(key)
        } catch (error) {
          console.error(`Error removing ${key} from localStorage:`, error)
        }
      }, WRITE_DELAY)
    },

    // Fixed subscribe implementation to match SyncStorage interface
    subscribe: (key: string, callback: (value: T) => void): (() => void) => {
      if (typeof window === 'undefined' || typeof window.addEventListener === 'undefined') {
        // Return no-op function when not in browser
        return () => {}
      }

      // Handle storage events from other tabs
      const handleStorageChange = (e: StorageEvent) => {
        if (e.storageArea === localStorage && e.key === key) {
          try {
            // Handle case when value is null (item was removed)
            const initialValue = memoryCache.get(key)
            const newValue = e.newValue ? (JSON.parse(e.newValue) as T) : initialValue

            // Update our memory cache
            memoryCache.set(key, newValue)

            // Trigger the atom update
            callback(newValue)
          } catch (error) {
            console.error(`Error handling storage event for ${key}:`, error)
          }
        }
      }

      // Subscribe to storage events (other tabs changing localStorage)
      window.addEventListener('storage', handleStorageChange)

      // Return unsubscribe function
      return () => {
        window.removeEventListener('storage', handleStorageChange)
      }
    }
  }
}

// Default lazy storage instance that works with any type
export const lazyStorage = {
  getItem: <T>(key: string, initialValue: T): T => {
    const storage = createLazyJSONStorage<T>(false)
    return storage.getItem(key, initialValue)
  },
  setItem: <T>(key: string, value: T): void => {
    const storage = createLazyJSONStorage<T>(false)
    storage.setItem(key, value)
  },
  removeItem: (key: string): void => {
    const storage = createLazyJSONStorage<any>(false)
    storage.removeItem(key)
  },
  subscribe: <T>(key: string, callback: (value: T) => void): (() => void) => {
    const storage = createLazyJSONStorage<T>(false)
    // Always return a function, even if subscribe might return undefined
    return storage.subscribe?.(key, callback) || (() => {})
  }
}

// Storage instance that reads from localStorage on init (for critical state)
export const criticalLazyStorage = {
  getItem: <T>(key: string, initialValue: T): T => {
    const storage = createLazyJSONStorage<T>(true)
    return storage.getItem(key, initialValue)
  },
  setItem: <T>(key: string, value: T): void => {
    const storage = createLazyJSONStorage<T>(true)
    storage.setItem(key, value)
  },
  removeItem: (key: string): void => {
    const storage = createLazyJSONStorage<any>(true)
    storage.removeItem(key)
  },
  subscribe: <T>(key: string, callback: (value: T) => void): (() => void) => {
    const storage = createLazyJSONStorage<T>(true)
    // Always return a function, even if subscribe might return undefined
    return storage.subscribe?.(key, callback) || (() => {})
  }
}
